import { AssetsProvider } from '@/components/contexts/AssetsProvider'
import { Overview } from '@/components/syllabus-tabs/Overview'
import { Syllabus } from '@/kontent/content-types'
import {
	CommonContentTab,
	getCommonSyllabusData,
	getLayout,
	getStaticPaths,
} from '@/pages/learning-areas/[learningarea]/[syllabus]/[tab]'
import { CommonSyllabusPathParams } from '@/pages/learning-areas/[learningarea]/[syllabus]/[tab]/[stage]/[...afterStageSlugs]'
import { CommonPageProps, CustomSyllabusTab } from '@/types'
import { filterPreviewableSyllabusesOnly } from '@/utils'
import { cleanJson } from '@/utils/cleanJson'
import { getStaticPropsForRedirectTo404ForNonPreviewedSyllabus } from '@/utils/syllabus'
import { InferGetStaticPropsType } from 'next'
import { GetStaticProps } from 'next/types'

export const getStaticProps: GetStaticProps<
	CommonPageProps<
		Syllabus,
		CommonContentTab<Syllabus>,
		CommonSyllabusPathParams
	>,
	CommonSyllabusPathParams
> = async (context) => {
	const { params, preview } = context
	const {
		section,
		learningarea: learningareaSlug,
		syllabus: syllabusSlug,
	} = params

	const {
		config,
		mappings,
		syllabusesResponse,
		syllabusResponse,
		stages,
		years,
		stageGroups,
		keyLearningAreas,
		assets,
		webLinkExternals,
		webLinkVideos,
		defaultFocusAreaUrls,
		disabledStages,
	} =
		(await getCommonSyllabusData({
			...context,
			params: {
				...params,
				tab: 'overview',
			},
		})) || {}

	// redirect to 404 if the syllabus is not allowed to be (pre)viewed
	const redirectPropsIfSyllabusIsNotPublished =
		getStaticPropsForRedirectTo404ForNonPreviewedSyllabus(
			syllabusResponse.item,
			`/404?path=/learning-areas/${[
				learningareaSlug,
				syllabusSlug,
				'overview',
				section,
			].join('/')}`,
		)
	if (redirectPropsIfSyllabusIsNotPublished) {
		return redirectPropsIfSyllabusIsNotPublished
	}

	const _props: InferGetStaticPropsType<typeof getStaticProps> = {
		mappings,
		preview: !!preview,
		previewData: null,
		params: {
			...params,
			slug: [
				'learning-areas',
				params.learningarea,
				params.syllabus,
				'overview',
				section as unknown as string,
			],

			// TODO: probably need to be removed. This is just to
			tab: 'overview',
		},
		data: {
			config,
			pageResponse: null,
			syllabuses: {
				...syllabusesResponse,
				items: syllabusesResponse.items.filter(
					filterPreviewableSyllabusesOnly,
				),
			},
			syllabus: syllabusResponse,
			focusArea: null,
			stages,
			years,
			stageGroups,
			keyLearningAreas,
			glossaries: null,
			assets: (
				[
					'overview',
					'teaching-and-learning',
					'content',
					'assessment',
				] as CustomSyllabusTab[]
			).includes('overview' as any)
				? assets
				: [],
			webLinkExternals,
			webLinkVideos,
			defaultFocusAreaUrls,
			disabledStages,
		},
	}

	return {
		props: {
			rootLayoutClassName: 'max-w-none mx-0 px-0 !pt-0',
			...cleanJson(_props),
		},
	}
}

export default function Section(props) {
	return (
		<AssetsProvider assets={props.data.assets}>
			<Overview {...props}></Overview>
		</AssetsProvider>
	)
}

Section.getLayout = getLayout

export { getStaticPaths }
